import React, { useState } from 'react';
import { toast } from 'sonner';
import { AlarmClock, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { aiApi } from '@/utils/aiApi';
import { stripMarkdown } from '@/utils/markdownText';

const pad = (n) => String(n).padStart(2, '0');
const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const formatWhen = (date) => date.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export const reminderPresets = (now = new Date()) => {
  const inHour = new Date(now.getTime() + 3600e3);
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1); tomorrow.setHours(9, 0, 0, 0);
  const monday = new Date(now); monday.setDate(now.getDate() + (((8 - now.getDay()) % 7) || 7)); monday.setHours(9, 0, 0, 0);
  return [
    { id: 'hour', label: 'In 1 hour', at: inHour },
    { id: 'tomorrow', label: 'Tomorrow 9:00', at: tomorrow },
    { id: 'monday', label: 'Next Monday 9:00', at: monday },
  ];
};

// "Remind me" about an answer: preset or custom time + editable note → Vibely AI pings you in Communications.
export const RemindMenu = ({ message, chatId, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [custom, setCustom] = useState('');
  const [saving, setSaving] = useState(false);

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) {
      setNote(stripMarkdown(message.content).replace(/\s+/g, ' ').slice(0, 160).trim());
      setCustom(toLocalInput(new Date(Date.now() + 3600e3)));
    }
  };

  const save = async (at) => {
    if (!note.trim()) { toast.error('Add a short note for the reminder'); return; }
    if (!(at instanceof Date) || Number.isNaN(at.getTime())) { toast.error('Pick a valid date and time'); return; }
    setSaving(true);
    try {
      const reminder = await aiApi.createReminder({ text: note.trim(), due_at: at.toISOString(), chat_id: chatId, message_id: message.id });
      toast.success(`Reminder set for ${formatWhen(at)} — Vibely AI will ping you in Communications`);
      onCreated?.(reminder);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not set the reminder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" className={`chat-action with-label ${open ? 'active' : ''}`} aria-label="Remind me about this answer" title="Remind me" data-testid={`chat-remind-${message.id}`}>
          <AlarmClock size={13} /> <span>Remind me</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="remind-menu" data-testid={`remind-menu-${message.id}`}>
        <div className="remind-menu-title">Remind me about this</div>
        <label className="remind-field">
          <span>Note</span>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} data-testid="remind-note" />
        </label>
        <div className="remind-presets">
          {reminderPresets().map((p) => (
            <Button key={p.id} type="button" variant="outline" size="sm" disabled={saving} onClick={() => save(p.at)} data-testid={`remind-preset-${p.id}`}>
              {p.label}
            </Button>
          ))}
        </div>
        <label className="remind-field">
          <span>Custom date &amp; time</span>
          <div className="remind-custom">
            <Input type="datetime-local" value={custom} min={toLocalInput(new Date())} onChange={(e) => setCustom(e.target.value)} data-testid="remind-custom-input" />
            <Button type="button" size="sm" disabled={saving || !custom} onClick={() => save(new Date(custom))} data-testid="remind-custom-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : 'Set'}
            </Button>
          </div>
        </label>
        <p className="remind-hint">Delivered as a message from Vibely AI in Communications, in your device's time zone.</p>
      </PopoverContent>
    </Popover>
  );
};
